import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Package } from '@phosphor-icons/react';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    planificado: 0,
    notificado: 0,
    en_anden: 0,
    cargando: 0,
    en_transito: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Obtener fecha de hoy en formato YYYY-MM-DD
      const today = new Date().toISOString().split('T')[0];
      
      // Consultar viajes del día por cada estado
      const [planificadoRes, notificadoRes, enAndenRes, cargandoRes, enTransitoRes] = await Promise.all([
        api.get(`/shipments?estado=planificado&fecha=${today}`).catch(() => ({ data: [] })),
        api.get(`/shipments?estado=notificado&fecha=${today}`).catch(() => ({ data: [] })),
        api.get(`/shipments?estado=en_anden&fecha=${today}`).catch(() => ({ data: [] })),
        api.get(`/shipments?estado=cargando&fecha=${today}`).catch(() => ({ data: [] })),
        api.get(`/shipments?estado=en_transito&fecha=${today}`).catch(() => ({ data: [] })),
      ]);

      setStats({
        planificado: planificadoRes.data.length,
        notificado: notificadoRes.data.length,
        en_anden: enAndenRes.data.length,
        cargando: cargandoRes.data.length,
        en_transito: enTransitoRes.data.length,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Planificado', value: stats.planificado, icon: Package, color: 'cyan', bgColor: 'bg-cyan-500', testid: 'stat-planificado' },
    { label: 'Notificado', value: stats.notificado, icon: Package, color: 'blue', bgColor: 'bg-blue-600', testid: 'stat-notificado' },
    { label: 'En Andén', value: stats.en_anden, icon: Package, color: 'yellow', bgColor: 'bg-yellow-500', testid: 'stat-en-anden' },
    { label: 'Cargando', value: stats.cargando, icon: Package, color: 'orange', bgColor: 'bg-orange-500', testid: 'stat-cargando' },
    { label: 'En Tránsito', value: stats.en_transito, icon: Package, color: 'purple', bgColor: 'bg-purple-600', testid: 'stat-en-transito' },
  ];

  const colorMap = {
    cyan: 'text-cyan-700 bg-cyan-50 border-cyan-200',
    blue: 'text-blue-700 bg-blue-50 border-blue-200',
    yellow: 'text-yellow-700 bg-yellow-50 border-yellow-200',
    orange: 'text-orange-700 bg-orange-50 border-orange-200',
    purple: 'text-purple-700 bg-purple-50 border-purple-200',
  };

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-2">
          Wallboard · {user?.rol?.replace('_', ' ')}
        </p>
        <h1 className="text-4xl font-bold text-neutral-900 tracking-tight" data-testid="dashboard-title">
          Hola, {user?.nombre?.split(' ')[0]}
        </h1>
        <p className="mt-2 text-neutral-600">
          Panel de control · Viajes del día
        </p>
      </div>

      {/* Stats Grid - Viajes del Día por Estado */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              data-testid={stat.testid}
              className="bg-white border border-neutral-200 rounded-md p-6 hover:border-neutral-400 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-3 h-3 rounded-full ${stat.bgColor}`}></div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
                  {stat.label}
                </p>
                <p className="text-4xl font-bold text-neutral-900 tracking-tight">
                  {loading ? '—' : stat.value}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-md p-8">
          <p className="text-xs uppercase tracking-widest text-neutral-500 mb-3">
            Fase 1 · Fundación
          </p>
          <h2 className="text-2xl font-bold text-neutral-900 tracking-tight mb-4">
            Sistema Multi-Empresa Activo
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-start">
              <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 mr-3 shrink-0"></div>
              <div>
                <p className="font-medium text-neutral-900">Autenticación JWT</p>
                <p className="text-neutral-600 text-xs mt-1">Roles y permisos por empresa</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 mr-3 shrink-0"></div>
              <div>
                <p className="font-medium text-neutral-900">Emulador WhatsApp</p>
                <p className="text-neutral-600 text-xs mt-1">Testing sin API real</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 mr-3 shrink-0"></div>
              <div>
                <p className="font-medium text-neutral-900">Gemini 3.5 Flash</p>
                <p className="text-neutral-600 text-xs mt-1">OCR multi-idioma preparado</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 mr-3 shrink-0"></div>
              <div>
                <p className="font-medium text-neutral-900">Object Storage</p>
                <p className="text-neutral-600 text-xs mt-1">Evidencias fotográficas</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-neutral-950 text-white rounded-md p-8">
          <p className="text-xs uppercase tracking-widest text-neutral-400 mb-3">
            Próximas fases
          </p>
          <h3 className="text-xl font-bold mb-4">Roadmap</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start">
              <span className="text-neutral-500 mr-2">02 →</span>
              <span>Integración SAP R/3 y S/4HANA</span>
            </li>
            <li className="flex items-start">
              <span className="text-neutral-500 mr-2">03 →</span>
              <span>PWA Conductores</span>
            </li>
            <li className="flex items-start">
              <span className="text-neutral-500 mr-2">04 →</span>
              <span>WhatsApp Business Real</span>
            </li>
            <li className="flex items-start">
              <span className="text-neutral-500 mr-2">05 →</span>
              <span>Dashboard Real-time</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
